/* Pure helpers. No DOM, no state: everything here is unit-testable. */

export const now = () => Date.now();
export const today = () => new Date().toDateString();
export const esc = s => String(s == null ? "" : s)
  .replace(/[&<>"]/g, m => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
export const jsEsc = s => esc(s).replace(/'/g, "\\'");
export function shuffle(a){a=a.slice();for(let i=a.length-1;i>0;i--){const j=(Math.random()*(i+1))|0;[a[i],a[j]]=[a[j],a[i]];}return a;}

export const full=c=>c.art?c.art+" "+c.term:c.term;
/* Answer matching: ignore case, ß/ss, a leading article, and any spacing or
   hyphen used to show a separable prefix ("ab-sagen" = "ab sagen" = "absagen"). */
export const norm=s=>String(s).toLowerCase().replace(/ß/g,"ss").trim()
  .replace(/^(der|die|das)\s+/,"").replace(/[\s\u00b7-]+/g,"");

export const DET=/^(der|die|das|den|dem|des|ein|eine|einen|einem|eines|einer|mein|meine|meinen|meinem|dein|deine|sein|seine|seinen|ihr|ihre|ihren|kein|keine|keinen|unser|unsere|dieser|diese|dieses|diesem|diesen)$/i;
const letters = (t) => String(t || "").replace(/[^\p{L}]/gu, "");

/* Prefixes that detach in a main clause: "aufwachen" -> "wache ... auf".
   Deliberately conservative: only ones that are reliably separable. */
export const SEP_PREFIXES = ["zurück","zusammen","wieder","weiter","vorbei","herunter","hinunter",
  "heraus","hinaus","herein","hinein","auf","aus","ein","mit","nach","vor","weg","zu","ab","an","bei",
  "fest","her","hin","los","frei","heim","fort","statt","teil"];

/* Users write separable verbs many ways: "ab-sagen", "ab|sagen", "ab sagen".
   Store one canonical form and remember the prefix. */
export function parseSeparable(term) {
  const raw = String(term || "").trim();
  const split = raw.match(/^(\p{L}+)\s*[-|·]\s*(\p{L}{3,})$/u);
  if (split && SEP_PREFIXES.includes(split[1].toLowerCase())) {
    return { term: split[1].toLowerCase() + split[2], sep: split[1].toLowerCase() };
  }
  return { term: raw, sep: detectSeparable(raw) };
}

/** The separable prefix of an infinitive, or null. Verbs only. */
export function detectSeparable(term) {
  const t = String(term || "").trim().toLowerCase().replace(/^sich\s+/, "");
  if (!/(en|eln|ern)$/.test(t)) return null;
  for (const p of SEP_PREFIXES) {
    if (t.length > p.length + 3 && t.startsWith(p)) return p;
  }
  return null;
}

/** "absagen" -> "ab·sagen", so the split is visible at a glance. */
export function displayTerm(c) {
  const sep = c && c.pos === "v" ? (c.sep || detectSeparable(c.term)) : null;
  if (!sep) return String(c && c.term || "");
  const t = String(c.term);
  return t.slice(0, sep.length) + "\u00b7" + t.slice(sep.length);
}

/**
 * The full plural, expanding the dictionary shorthand people actually write:
 *   "-e" -> Bleistifte      "-"  -> Gebäude (unchanged)
 *   "-̈er"/"¨er" -> Häuser   ""/"—" -> no plural
 * A value that isn't shorthand is taken as the finished word.
 */
export function pluralForm(c) {
  if (!c || c.pos !== "n") return null;
  const raw = String(c.plural || "").trim();
  if (!raw || raw === "—" || raw === "-—") return null;
  const term = String(c.term || "");
  if (raw === "-") return term;
  const m = raw.match(/^[-–]?\s*([\u00a8\u0308]?)\s*(\p{L}*)$/u);
  if (raw.startsWith("-") || raw.startsWith("–") || /^[\u00a8\u0308]/.test(raw)) {
    const umlaut = m && m[1];
    const suffix = m ? m[2] : raw.replace(/^[-–\u00a8\u0308]+/, "");
    let stem = term;
    if (umlaut) stem = umlautLast(stem);
    return stem + suffix;
  }
  return raw;
}
/* Umlaut the stem vowel, which is what "¨er" notation means. "au" umlauts as
   a unit (Haus -> Häuser), so it's checked before the single vowels. */
function umlautLast(w) {
  const au = w.lastIndexOf("au");
  if (au >= 0) return w.slice(0, au) + "äu" + w.slice(au + 2);
  const i = Math.max(w.lastIndexOf("a"), w.lastIndexOf("o"), w.lastIndexOf("u"));
  if (i < 0) return w;
  return w.slice(0, i) + { a: "ä", o: "ö", u: "ü" }[w[i]] + w.slice(i + 1);
}

/* Separable prefixes that can sit in front of the participle "ge-":
   auf+ge+macht, zurück+ge+kommen. */
const GE = /^(ab|an|auf|aus|bei|ein|fest|her|hin|los|mit|nach|vor|weg|zu|zurück)?ge(?=\p{L}{3})/u;
/* ...and the "zu" of an infinitive clause: auf+zu+wachen. */
const ZU = /^(ab|an|auf|aus|bei|ein|fest|her|hin|los|mit|nach|vor|weg|zurück)zu(?=\p{L}{3})/u;

/* Plurals and comparatives often add an umlaut (Haus -> Häuser, Buch ->
   Bücher), which would otherwise break a prefix match. */
const fold = (t) => String(t).toLowerCase()
  .replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u").replace(/ß/g, "ss");

function stemMatch(word, stem, allowAffix = true) {
  const w = fold(word);
  stem = fold(stem);
  if (w.length >= stem.length && w.startsWith(stem)) return true;
  if (!allowAffix) return false;
  /* Regular past participles start with ge-, so "gekauft" doesn't begin with
     "kauf". Strip it (and any separable prefix before it) and look again. */
  for (const rx of [GE, ZU]) {
    const bare = w.replace(rx, "$1");
    if (bare !== w && bare.startsWith(stem)) return true;
  }
  return false;
}

const stemsOf = (base, pos) => {
  const n = Math.max(4, Math.min(5, base.length - 2));
  const out = [base.slice(0, n)];
  /* The shorter fallback is for irregular VERB forms (vermeiden -> vermieden).
     For nouns it caused false matches (Fahrrad vs gefahren), so it's verbs only. */
  if (n > 4 && pos === "v") out.push(base.slice(0, 4));
  return out;
};

/**
 * Which token(s) Lückentext should hide, as indices into the whitespace-split
 * token array. Usually one. A separable verb whose prefix has detached
 * ("wache ... auf") returns two, because hiding only half gives it away.
 * Returns [] when the word isn't findable.
 */
function locate(c, toks) {
  if (c.gap) {
    const g = fold(c.gap);
    for (let i = 0; i < toks.length; i += 2) if (fold(letters(toks[i])) === g) return [i];
  }
  const base = String(c.term || "").replace(/^sich\s+/, "");
  const stems = stemsOf(base, c.pos);
  for (let s = 0; s < stems.length; s++) {
    for (let i = 0; i < toks.length; i += 2) {
      const w = letters(toks[i]);
      if (!stemMatch(w, stems[s], s === 0)) continue;
      if (s > 0 && Math.abs(w.length - base.length) > 4) continue;
      return [i];
    }
  }
  /* Nothing whole. If it's a separable verb, the prefix may be parked at the
     end of the clause: "Ich wache nachts dauernd auf." */
  const sep = c.pos === "v" ? (c.sep || detectSeparable(c.term)) : null;
  if (sep) {
    const rest = base.slice(sep.length);
    /* Strip the infinitive ending to get the real verb stem: "sagen" -> "sag",
       so the conjugated "sagt" matches. stemsOf's 4-5 letter window is too
       coarse for short verbs. Safe here because the detached prefix must also
       be present for this to count as a hit. */
    const rStems = [rest.replace(/e?n$/, "")].filter((x) => x.length >= 3);
    for (const stem of rStems) {
      for (let i = 0; i < toks.length; i += 2) {
        if (!stemMatch(letters(toks[i]), stem, false)) continue;
        for (let j = 0; j < toks.length; j += 2) {
          if (j === i) continue;
          if (fold(letters(toks[j])) !== fold(sep)) continue;
          return i < j ? [i, j] : [j, i];
        }
      }
    }
  }
  return [];
}

export function cloze(c){
  if(!c||!c.ex)return null;
  const toks=c.ex.split(/(\s+)/);
  const hits=locate(c,toks);
  if(!hits.length)return null;
  const i=hits[0];
  const w=letters(toks[i]);
  let from=i;
  if(hits.length===1&&i>=2&&c.pos==="n"){   /* only for nouns; an adjective's article belongs to the noun after it */
    if(DET.test(letters(toks[i-2])))from=i-2;
  }
  /* Every hidden span, in order. Two for a split separable verb. */
  const ranges=hits.length>1?[[hits[0],hits[0]],[hits[1],hits[1]]]:[[from,i]];
  const hidden=new Set();
  ranges.forEach(([a,b])=>{for(let k=a;k<=b;k+=2)hidden.add(k);});
  let words=0;
  for(let k=0;k<toks.length;k+=2)
    if(!hidden.has(k)&&letters(toks[k]))words++;
  if(words<4)return null;
  return{toks,from,i,w,det:from!==i,ranges,split:hits.length>1,
         parts:ranges.map(([a])=>letters(toks[a]))};
}

/**
 * Can Lückentext use this sentence, and if not, why? The word editor and the
 * AI enrichment both use this, so they apply exactly the rule the game does.
 * @returns {{cz: object|null, reason: null|"empty"|"missing"|"short"}}
 */
export function clozeStatus(c) {
  if (!c || !String(c.ex || "").trim()) return { cz: null, reason: "empty" };
  const cz = cloze(c);
  if (cz) return { cz, reason: null };
  return { cz: null, reason: locate(c, String(c.ex).split(/(\s+)/)).length ? "short" : "missing" };
}

/**
 * Parse the German column as people actually write it:
 *   "der Bleistift, -e"  -> {art:"der", term:"Bleistift", plural:"-e"}
 *   "ab-sagen"           -> {term:"absagen", sep:"ab"}
 */
export function parseGermanField(raw) {
  let t = String(raw || "").trim();
  let art = null, plural = "";
  const a = t.match(/^(der|die|das)\s+(.+)$/i);
  if (a) { art = a[1].toLowerCase(); t = a[2].trim(); }
  const p = t.match(/^(.+?)\s*,\s*(.+)$/);
  if (p) { t = p[1].trim(); plural = p[2].trim(); }
  const sp = parseSeparable(t);
  return { art, term: sp.term, sep: sp.sep, plural };
}
